import { LOAD_MARKER_FAILURE, LOAD_MARKER_REQUEST, LOAD_MARKER_SUCCESS } from "../action-types.js";
import { type ReduxAction, createReducer } from "../redux-utils.js";
import { createErrorHandler } from "@/app-utils/errors.ts";
import { type Marker } from "@/graphql.ts";

/**
 * State definition
 */
export interface IMarkerState {
  marker?: Marker;
  isLoading: boolean;
}

const initialState: IMarkerState = {
  marker: undefined,
  isLoading: true,
};

/**
 * Reducers
 */
const markerReducer = createReducer(initialState, {
  [LOAD_MARKER_REQUEST]: (state: IMarkerState): IMarkerState => {
    return { ...state, isLoading: true };
  },
  [LOAD_MARKER_SUCCESS]: (state: IMarkerState, payload: Marker): IMarkerState => {
    return { ...state, isLoading: false, marker: payload };
  },
  [LOAD_MARKER_FAILURE]: (state: IMarkerState): IMarkerState => {
    return { ...state, isLoading: false, marker: undefined };
  },
});

/**
 * Action creators
 */

// Last request in time unique identifier
let lastFetchedMarkerId = -1;

function loadMarker(markerId: number): ReduxAction<Promise<IMarkerState>> {
  return (dispatch, getState, webApiClient) => {
    const state = getState();
    lastFetchedMarkerId = markerId;

    // fetch the gene only if no loading is currently in progress and gene is either absent or doesn't match the gene ID
    if (!state.marker.marker || state.marker.marker.id !== markerId) {
      dispatch({ type: LOAD_MARKER_REQUEST });

      return webApiClient
        .fetchMarker(markerId)
        .then((marker) => {
          if (lastFetchedMarkerId === marker.id) {
            dispatch({
              type: LOAD_MARKER_SUCCESS,
              payload: marker,
            });
            return marker;
          }

          return state.marker.marker;
        })
        .catch(
          createErrorHandler(dispatch, {
            actionType: LOAD_MARKER_FAILURE,
            alertTitle: "Fetching of Marker Failed",
          }),
        );
    } else {
      // We reset the loading flag to not stay on a loading state indefinitely
      // because lastFetchId can be not synchronized from state.gene.gene.id
      dispatch({
        type: LOAD_MARKER_SUCCESS,
        payload: state.marker.marker,
      });

      return Promise.resolve(state.marker.marker);
    }
  };
}

export { loadMarker, markerReducer };
